// BatchAndFees.jsx

import { useEffect, useMemo, useState } from "react";
import { studentDetailsDeletedBatchNFeeDetails } from "../../../../api/StudentDetails";
import { SweetAlertShow } from "../../../master/Master";
import { Link, useNavigate } from "react-router-dom";

import {
  batchStatusMeta,
  feeStatusMeta,
  fmtMoney,
  Pill,
} from "../components/Master";
import FeeDetailModal from "../components/FeeDetailModal";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);



const SummaryCard = ({
  icon,
  color,
  label,
  value,
  strikeValue,
  badgeValue,
  loading,
}) => (
  <div className="col-lg-4 col-md-6">
    <div
      className="p-3 bg-white rounded-3 h-100 d-flex align-items-center gap-3"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div
        className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10 flex-shrink-0`}
        style={{ width: 56, height: 56 }}
      >
        <i
          className={`${icon} text-${color === "primary" ? "white" : color}`}
          style={{ fontSize: 24 }}
        ></i>
      </div>

      <div>
        <small className="text-secondary">{label}</small>

        {loading ? (
          <div className="placeholder-glow mt-1">
            <Skeleton w="100px" h="24px" />
          </div>
        ) : (
          <>
            {strikeValue && (
              <div>
                <small className="text-decoration-line-through text-muted">
                  {strikeValue}
                </small>
              </div>
            )}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h4 className={`mb-0 fw-bold text-${color}`}>{value}</h4>
              {badgeValue && (
                <span
                  className="text-warning fw-semibold"
                  style={{ fontSize: 14 }}
                >
                  ({badgeValue} Discount)
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "-";

export default function BatchAndFeesDeleted({ id }) {

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [selectedBatch, setSelectedBatch] = useState(null);
  const navigate = useNavigate();


  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsDeletedBatchNFeeDetails(student_id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id);
  }, [id]);

  const batches = data?.student_deleted_batch_n_fees || [];

  const sortedBatches = [...batches].sort(
    (a, b) =>
      new Date(b.batch_enroll_date || 0).getTime() -
      new Date(a.batch_enroll_date || 0).getTime(),
  );
  const overallTotals = useMemo(() => {
    return batches.reduce(
      (acc, b) => ({
        totalFee: acc.totalFee + Number(b.total_fee || 0),
        discount: acc.discount + Number(b.total_discount || 0),
        finalFee: acc.finalFee + Number(b.final_fee || 0),
        paid: acc.paid + Number(b.total_paid || 0),
        due: acc.due + Number(b.total_due || 0),
      }),
      { totalFee: 0, discount: 0, finalFee: 0, paid: 0, due: 0 },
    );
  }, [batches]);

  const goToTransfer = (batch) => {
    const qs = new URLSearchParams({
      batch: batch?.batch?.id ?? "",
      student_id: id ?? "",
      batch_n_fee_id: batch?.id ?? "",
    }).toString();
    navigate(`/student/batch/transfer?${qs}`);
  };

  if (loading) {
    return (
      <div className="d-flex flex-column gap-3">
        <div className="row g-3">
          {[1, 2, 3].map((n) => (
            <div className="col-lg-4 col-md-6" key={n}>
              <div
                className="p-3 bg-white rounded-3 d-flex align-items-center gap-3"
                style={{ border: "1px solid #eef0f4" }}
              >
                <Skeleton w="56px" h="56px" />
                <div className="placeholder-glow d-flex flex-column gap-2 flex-grow-1">
                  <Skeleton h="14px" w="80px" />
                  <Skeleton h="24px" w="110px" />
                </div>
              </div>
            </div>
          ))}
        </div>
        {[1, 2].map((n) => (
          <div
            key={n}
            className="p-4 bg-white rounded-3"
            style={{ border: "1px solid #eef0f4" }}
          >
            <div className="placeholder-glow d-flex gap-2 mb-4">
              <Skeleton w="220px" h="28px" />
              <Skeleton w="160px" h="28px" />
              <Skeleton w="120px" h="28px" />
            </div>
            <div className="placeholder-glow mb-3">
              <Skeleton h="8px" />
            </div>
            <div className="row g-3">
              {[1, 2, 3, 4].map((c) => (
                <div className="col-3" key={c}>
                  <div className="placeholder-glow d-flex flex-column gap-2">
                    <Skeleton h="14px" />
                    <Skeleton h="20px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!sortedBatches.length) {
    return (
      <div
        className="p-5 bg-white rounded-3 text-center text-muted"
        style={{ border: "1px solid #eef0f4" }}
      >
        <i className="ri-wallet-3-line d-block fs-1 mb-2"></i>
        <p className="mb-0">No batch or fee details available</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {/* Overall Summary - matches screenshot design */}
      <div className="row g-3">
        <SummaryCard
          icon="ri-wallet-3-line"
          color="primary"
          label="Total Payable Fee"
          strikeValue={
            overallTotals.discount > 0 ? fmtMoney(overallTotals.totalFee) : null
          }
          value={fmtMoney(overallTotals.finalFee)}
          badgeValue={
            overallTotals.discount > 0 ? fmtMoney(overallTotals.discount) : null
          }
          loading={loading}
        />
        <SummaryCard
          icon="ri-checkbox-circle-line"
          color="success"
          label="Total Receive"
          value={fmtMoney(overallTotals.paid)}
          loading={loading}
        />
        <SummaryCard
          icon="ri-error-warning-line"
          color="danger"
          label="Total Due"
          value={fmtMoney(overallTotals.due)}
          loading={loading}
        />
      </div>

      {/* Per-batch cards */}
      {sortedBatches.map((batch) => {
        const feeDetails = batch?.student_fee_details || [];
        const prevSessionFees = feeDetails.filter(
          (f) => f.fee_type === "Previous Session Fees",
        );
        const currentFees = feeDetails.filter(
          (f) => f.fee_type !== "Previous Session Fees",
        );

        const currentTotal = currentFees.reduce(
          (s, f) => s + Number(f.actual_amount || 0),
          0,
        );
        const currentDiscount = currentFees.reduce(
          (s, f) => s + Number(f.discount || 0),
          0,
        );
        const currentFinal = currentFees.reduce(
          (s, f) => s + Number(f.final_amount || 0),
          0,
        );

        const prevTotal = prevSessionFees.reduce(
          (s, f) => s + Number(f.actual_amount || 0),
          0,
        );
        const prevPaid = prevSessionFees.reduce(
          (s, f) => s + Number(f.paid_amount || 0),
          0,
        );
        const prevDue = prevSessionFees.reduce(
          (s, f) => s + Number(f.due_amount || 0),
          0,
        );

        const batchPaid = Number(batch?.total_paid || 0);
        const batchFinal = Number(batch?.final_fee || 0);
        const batchDue = Number(batch?.total_due || 0);
        const batchPct = batchFinal
          ? Math.min(100, Math.round((batchPaid / batchFinal) * 100))
          : 0;

        const statusMeta = batchStatusMeta(batch?.status);
        const isActiveBatch = batch?.status === "1";
        const hasTransferHistory = Number(batch?.transfer_history_count || 0) > 0;


        return (
          <div
            key={batch.id}
            className="p-4 bg-white rounded-3"
            style={{ border: "1px solid #eef0f4" }}
          >
            {/* Header pills */}
            <div className="row justify-content-between align-items-start flex-wrap g-3 mb-3">
              <div className="col-md-8 d-flex flex-wrap gap-2">
                <Pill
                  icon="ri-book-open-line"
                  color="primary"
                  label="Course"
                  value={batch?.course?.title || "-"}
                />
                <Pill
                  icon="ri-team-line"
                  color="success"
                  label="Batch"
                  value={batch?.batch?.title || "-"}
                />
                <Pill
                  icon="ri-calendar-line"
                  color="info"
                  label="Session"
                  value={batch?.session_year?.session || "-"}
                />
                <Pill
                  icon="ri-hashtag"
                  color="warning"
                  label="Id"
                  value={batch?.id}
                />
                {batch?.rte_status === "RTE" && (
                  <Pill
                    icon="ri-government-line"
                    color="danger"
                    label="RTE Status"
                    value="RTE"
                  />
                )}

                <span
                  className={`badge rounded-pill bg-${statusMeta.color} d-inline-flex align-items-center gap-1 px-3 py-2`}
                >
                  <i className={statusMeta.icon}></i>
                  {statusMeta.label}
                </span>

                {hasTransferHistory && (
                  <span
                    className="badge rounded-pill bg-label-info d-inline-flex align-items-center gap-1 px-3 py-2"
                    title="View Transfer History"
                  >
                    <i className="ri-history-line"></i>
                    Transferred ({batch.transfer_history_count})
                  </span>
                )}
              </div>

              <div className="col-md-4 text-end">
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <i
                    className="ri-add-circle-line text-primary cursor-pointer fs-3"
                    title="Manage Subjects & Fees"
                    onClick={() =>
                      navigate(`/student/subjects/Fee/Up?id=${id}&batch_n_fees_id=${batch.id}`)
                    }
                  />

                  {isActiveBatch && (
                    <i
                      className="ri-shuffle-line text-primary cursor-pointer fs-3"
                      title="Transfer to Another Batch"
                      onClick={() => goToTransfer(batch)}
                    />
                  )}

                  <small className="d-flex align-items-center gap-2 mb-0">
                    <i
                      className="ri-eye-line cursor-pointer fs-3 text-primary"
                      title="View Fee Details"
                      onClick={() => setSelectedBatch(batch)}
                    />
                    PAYABLE FEE
                  </small>
                </div>

                {currentDiscount > 0 && (
                  <small className="text-decoration-line-through text-muted d-block">
                    {fmtMoney(currentTotal)}
                  </small>
                )}
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <h3 className="mb-0 fw-bold" style={{ color: "#6d5ee0" }}>
                    {fmtMoney(currentFinal)}
                  </h3>
                  {currentDiscount > 0 && (
                    <span className="badge bg-warning bg-opacity-10 text-warning">
                      {fmtMoney(currentDiscount)} discount
                    </span>
                  )}
                </div>
                {prevTotal > 0 && (
                  <span className="badge bg-secondary bg-opacity-10 text-secondary mt-1">
                    <i className="ri-add-line me-1"></i>
                    Previous Session Fees ({fmtMoney(prevTotal)})
                  </span>
                )}
              </div>
            </div>

            {/* Progress */}
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-secondary">
                Received {fmtMoney(batchPaid)} of {fmtMoney(batchFinal)}
              </small>
              <small
                className={`fw-semibold text-${batchPct >= 100 ? "success" : batchPct > 0 ? "warning" : "danger"}`}
              >
                {batchPct}%
              </small>
            </div>
            <div className="progress mb-3" style={{ height: 8 }}>
              <div
                className={`progress-bar bg-${batchPct >= 100 ? "success" : "warning"}`}
                style={{
                  width: `${batchPct}%`,
                  background: batchPct === 0 ? "#e5e0fb" : undefined,
                }}
              ></div>
            </div>

            {/* Previous Session Fees box */}
            {prevSessionFees.length > 0 && (
              <div
                className="pt-3 pb-3"
                style={{ borderBottom: "1px solid #f0f0f0" }}
              >
                <small
                  className="text-secondary fw-semibold d-flex align-items-center gap-1 mb-2"
                  style={{ letterSpacing: "0.5px" }}
                >
                  <i className="ri-history-line"></i> PREVIOUS SESSION FEES
                </small>
                <div className="row g-3">
                  <div className="col-lg-3 col-6">
                    <small className="text-secondary">Total</small>
                    <h6 className="mb-0 fw-bold">{fmtMoney(prevTotal)}</h6>
                  </div>
                  <div className="col-lg-3 col-6">
                    <small className="text-secondary">Remark</small>
                    <h6 className="mb-0 fw-bold">
                      {batch?.previous_session_due_fees_remark || "-"}
                    </h6>
                  </div>
                  <div className="col-lg-3 col-6">
                    <small className="text-secondary">Receive Fee</small>
                    <h6 className="mb-0 fw-bold text-success">
                      {fmtMoney(prevPaid)}
                    </h6>
                  </div>
                  <div className="col-lg-3 col-6">
                    <small className="text-secondary">Due</small>
                    <h6 className="mb-0 fw-bold text-danger">
                      {fmtMoney(prevDue)}
                    </h6>
                  </div>
                </div>
              </div>
            )}

            {/* Class / Counselor Info */}
            <div
              className="d-flex flex-wrap gap-4 pt-3"
              style={{
                borderBottom: feeDetails.length ? "1px solid #f0f0f0" : "none",
                paddingBottom: feeDetails.length ? "1rem" : 0,
              }}
            >
              {batch?.counselor?.name && (
                <small className="text-secondary d-flex align-items-center gap-1">
                  <i className="ri-user-star-line text-info"></i>
                  Counselor:
                  <span className="fw-medium text-dark">
                    {batch?.counselor?.name || "-"}
                  </span>
                </small>
              )}

              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-government-line text-danger"></i>
                RTE Status:
                <span
                  className={`fw-medium ${batch?.rte_status === "RTE" ? "text-danger" : "text-success"
                    }`}
                >
                  {batch?.rte_status || "-"}
                </span>
              </small>

              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-price-tag-3-line text-warning"></i>
                Discount Remark:
                <span className="fw-medium text-dark">
                  {batch?.discount_remark || "-"}
                </span>
              </small>

              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-calendar-check-line text-success"></i>
                Enrolled:
                <span className="fw-medium text-dark">
                  {fmtDate(batch?.batch_enroll_date)}
                </span>
              </small>

              {batch?.batch_end_date && (
                <small className="text-secondary d-flex align-items-center gap-1">
                  <i className="ri-calendar-close-line text-danger"></i>
                  Ends:
                  <span className="fw-medium text-dark">
                    {fmtDate(batch?.batch_end_date)}
                  </span>
                </small>
              )}

              {batch?.reason && (
                <small className="text-secondary d-flex align-items-center gap-1">
                  <i className="ri-chat-quote-line text-primary"></i>
                  Reason:
                  <span className="fw-medium text-dark">{batch.reason}</span>
                </small>
              )}
            </div>

            {/* Fee Structure Table */}
            {feeDetails.length > 0 && (
              <div className="pt-3">
                <small className="text-secondary fw-semibold d-flex align-items-center gap-1 mb-2">
                  <i className="ri-file-list-3-line"></i> FEE STRUCTURE
                </small>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Fee Type</th>
                        <th>Total</th>
                        <th>Discount</th>
                        <th>Final</th>
                        <th>Paid</th>
                        <th>Due</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeDetails.map((f, i) => {
                        const meta = feeStatusMeta(
                          f.paid_amount,
                          f.final_amount,
                        );
                        return (
                          <tr key={f.id}>
                            <td>{i + 1}</td>
                            <td>
                              <span className="d-flex align-items-center gap-1">
                                <i className="ri-price-tag-3-line text-secondary"></i>
                                {f.fee_type}
                              </span>
                            </td>
                            <td>{fmtMoney(f.actual_amount)}</td>
                            <td className="text-warning">
                              {fmtMoney(f.discount)}
                            </td>
                            <td>{fmtMoney(f.final_amount)}</td>
                            <td className="text-success">
                              {fmtMoney(f.paid_amount)}
                            </td>
                            <td className="text-danger">
                              {fmtMoney(f.due_amount)}
                            </td>
                            <td>
                              <span
                                className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} d-inline-flex align-items-center gap-1`}
                              >
                                <i className={meta.icon}></i>
                                {meta.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Fee Detail Modal */}
      {selectedBatch && (
        <>
          <FeeDetailModal
            batch={selectedBatch}
            onClose={() => setSelectedBatch(null)}
            onUpdated={() => {
              setSelectedBatch(null);
              getStudentDetailsMain(id);
            }}
          />
        </>
      )}
    </div>
  );
}
